import { useEffect, useState } from "react";
import type { TranslatedString } from "@i18n";
import { useTranslation, untranslated } from "@i18n";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";

import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { refreshFileManager } from "@Actions/fileUpload";

import {
  fetchRevisionSeries,
  fetchRevisionSeriesList,
  setRevisionSeries,
  type RevisionSeriesOption,
} from "../revisionSeriesShared";

type Props = { fileId: string };

function FileRevisionSeriesContent({ fileId }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [seriesList, setSeriesList] = useState<RevisionSeriesOption[]>([]);
  const [currentSeriesId, setCurrentSeriesId] = useState<string | null>(null);
  const [pendingSeriesId, setPendingSeriesId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // The fetch can land after the panel has moved on to another file, and the guard that drops that
    // late result has to survive type narrowing: a plain `let cancelled = false` is only ever written
    // from the cleanup closure, so TS pins it to the literal `false` and the guard reads as statically
    // dead. `signal.aborted` is a real `boolean` the checker cannot collapse.
    const abortOnUnmount = new AbortController();
    (async () => {
      const [list, current] = await Promise.all([fetchRevisionSeriesList(), fetchRevisionSeries(fileId)]);
      if (abortOnUnmount.signal.aborted) {
        return;
      }
      // Read at a type that admits holes: `fetchRevisionSeriesList` types the array straight off the
      // API response without validating its elements, and every read below dereferences one.
      const fetched: Array<RevisionSeriesOption | null | undefined> = list;
      setSeriesList(fetched.filter((series) => series != null));
      setCurrentSeriesId(current.revisionSeriesId);
      setIsLoading(false);
    })();
    return () => {
      abortOnUnmount.abort();
    };
  }, [fileId]);

  const showToast = (message: TranslatedString, type: typeof TOAST_TYPE_SUCCESS | typeof TOAST_TYPE_ERROR) =>
    dispatch(showToastNotification({ content: message, type }));

  const applyChange = async (revisionSeriesId: string) => {
    setIsSubmitting(true);
    try {
      const response = await setRevisionSeries({ resourceId: fileId, revisionSeriesId });
      if (response.status) {
        setCurrentSeriesId(revisionSeriesId);
        dispatch(refreshFileManager(true));
        showToast(t("Revision Series updated"), TOAST_TYPE_SUCCESS);
      } else {
        // `errorMessage` is raw server copy, so it goes through `untranslated()` rather than `t()`.
        showToast(
          response.errorMessage ? untranslated(response.errorMessage) : t("The Revision Series could not be updated"),
          TOAST_TYPE_ERROR,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectionChange = (newValue: RevisionSeriesOption | null) => {
    if (!newValue || newValue._id === currentSeriesId) return;

    if (currentSeriesId == null) {
      applyChange(newValue._id);
      return;
    }

    setPendingSeriesId(newValue._id);
  };

  const closeConfirmation = () => setPendingSeriesId(null);

  const confirmChange = async () => {
    if (!pendingSeriesId) return;
    await applyChange(pendingSeriesId);
    closeConfirmation();
  };

  const selectedSeries = seriesList.find((s) => s._id === currentSeriesId) ?? null;

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex items-center justify-between gap-3">
        <Text className="text-sm font-medium">{t("Revision Series")}</Text>
        <div className="min-w-[14rem]">
          <AutoComplete
            options={seriesList}
            value={selectedSeries}
            disabled={isLoading || isSubmitting}
            // `AutoComplete` short-circuits a nullish option before it calls `getOptionLabel`
            // (`safeGetOptionLabel`, Mui_Form/AutoComplete.tsx). `isOptionEqualToValue` is handed to
            // MUI unwrapped, so it is dereference-safe for a different, checked reason: the option
            // side is `seriesList`, filtered of holes above, and MUI guards the value side at every
            // call site for a single-select (`useAutocomplete.js` :426 gates :444, and :1065 tests
            // `!= null`; the ungated :183 needs `filterSelectedOptions`, which is not set here).
            getOptionLabel={(option: RevisionSeriesOption) => option.name || ""}
            isOptionEqualToValue={(option: RevisionSeriesOption, value: RevisionSeriesOption) =>
              option._id === value._id
            }
            onChange={(_event: unknown, newValue: RevisionSeriesOption | null) => handleSelectionChange(newValue)}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Revision Series")} />}
          />
        </div>
      </div>

      {pendingSeriesId && (
        <ModalStateDialogComponent
          isVisible
          width="40%"
          headerTitle={<p className="text-2xl font-bold">{t("Change Revision Series")}</p>}
          onCancel={closeConfirmation}
          modelContent={
            <div className="flex flex-col gap-6">
              <span>{t("Are you sure you want to change Revision Series?")}</span>
              <div className="rounded-lg p-3 text-sm bg-banner_color_yellow_translucent">
                {t(
                  "Changing the Revision Series will reset the current Revision nomenclature. All Revisions will be renamed according to the new series.",
                )}
              </div>
            </div>
          }
          footerContent={
            <div className="flex gap-4">
              <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} loading={isSubmitting} onClick={confirmChange}>
                {t("Confirm")}
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeConfirmation}>
                {t("Cancel")}
              </ButtonComponent>
            </div>
          }
        />
      )}
    </div>
  );
}

export default FileRevisionSeriesContent;
